import React, { useState, useEffect } from 'react';
import { useAppStore } from '@/store';
import { useMyProfile } from '@/hooks/api';
import { Button } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { api } from '@/api';
import { cn } from '@/utils';

const UpdateSpecializationScreen: React.FC = () => {
  const { setActiveScreen, setITQuestParams, user } = useAppStore();
  const { data: profile } = useMyProfile();
  const [specializations, setSpecializations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [selectedSpecializationId, setSelectedSpecializationId] = useState<number | null>(null);
  const [selectedTechStackIds, setSelectedTechStackIds] = useState<number[]>([]);

  // Check if this is first time selection (user has no specializations yet)
  const isFirstTime = !profile?.specializations || profile.specializations.length === 0;

  useEffect(() => {
    TelegramUtils.configureBackButton(true, () => {
      setActiveScreen('skills-verification');
    });
    TelegramUtils.configureMainButton({ visible: false });
  }, [setActiveScreen]);

  useEffect(() => {
    loadSpecializations();
  }, []);

  const loadSpecializations = async () => {
    try {
      const data = await (api as any).getSpecializations(true);
      setSpecializations(data);
      console.log('✅ Loaded specializations:', data);
    } catch (error) {
      console.error('Failed to load specializations:', error);
      await TelegramUtils.showAlert('Не удалось загрузить специализации');
    } finally {
      setLoading(false);
    }
  };

  const handleSpecializationSelect = (specId: number) => {
    setSelectedSpecializationId(specId);
    setSelectedTechStackIds([]);
    TelegramUtils.haptic.selection();
  };

  const toggleTech = (techId: number) => {
    const updated = selectedTechStackIds.includes(techId)
      ? selectedTechStackIds.filter(id => id !== techId)
      : [...selectedTechStackIds, techId];
    setSelectedTechStackIds(updated);
    TelegramUtils.haptic.selection();
  };

  const handleSave = async () => {
    if (!selectedSpecializationId) {
      await TelegramUtils.showAlert('Выберите специализацию');
      return;
    }

    setSaving(true);

    try {
      // Save IT info
      await (api as any).updateITInfo({
        specializations: [{
          specialization_id: selectedSpecializationId,
        }],
        tech_stack_ids: selectedTechStackIds,
      });

      console.log('✅ Specialization updated');
      TelegramUtils.haptic.success();

      // Use current level or default to senior
      const declaredLevel = profile?.level?.toLowerCase() || 'senior';

      // Set params for ITQuest
      setITQuestParams({
        mode: 'retest',
        specializationId: selectedSpecializationId,
        declaredLevel,
      });

      // Navigate to IT Quest
      setActiveScreen('it-quest');
    } catch (error) {
      console.error('Failed to update specialization:', error);
      TelegramUtils.haptic.error();
      await TelegramUtils.showAlert('Не удалось обновить специализацию');
    } finally {
      setSaving(false);
    }
  };

  const selectedSpecialization = specializations.find(s => s.id === selectedSpecializationId);

  if (loading) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tg-bg safe-area-y p-6 pb-32">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => setActiveScreen('skills-verification')}
          className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity mb-4"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Назад</span>
        </button>
        <h1 className="text-3xl font-bold text-white mb-2">
          {isFirstTime ? 'Выбери специальность' : 'Обновить специальность'}
        </h1>
        <p className="text-neutral-400">
          {isFirstTime 
            ? 'Выбери специальность и стек, затем пройди тест'
            : 'После сохранения нужно будет пройти тест заново'
          }
        </p>
      </div>

      <div className="space-y-6">
        {/* Specialization Selection */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Специализация
          </label>
          <p className="text-xs text-neutral-500 mb-4">
            Выберите вашу специализацию
          </p>
          <div className="flex flex-wrap gap-2">
            {specializations.map((spec) => (
              <button
                key={spec.id}
                type="button"
                onClick={() => handleSpecializationSelect(spec.id)}
                className={cn(
                  'px-3 py-2 rounded-full text-sm font-medium transition-all',
                  selectedSpecializationId === spec.id
                    ? 'bg-white text-black'
                    : 'bg-white/10 text-white/70 border border-white/20 hover:bg-white/20'
                )}
              >
                {spec.name}
              </button>
            ))}
          </div>
        </div>

        {/* Tech Stack - only show when specialization is selected */}
        {selectedSpecialization && selectedSpecialization.tech_stack && selectedSpecialization.tech_stack.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Технологии
            </label>
            <p className="text-xs text-neutral-500 mb-4">
              Выберите ваш стек • {selectedTechStackIds.length} выбрано
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedSpecialization.tech_stack.map((tech: any) => (
                <button
                  key={tech.id}
                  type="button"
                  onClick={() => toggleTech(tech.id)}
                  className={cn(
                    'px-3 py-2 rounded-full text-sm font-medium transition-all',
                    selectedTechStackIds.includes(tech.id)
                      ? 'bg-white text-black'
                      : 'bg-white/10 text-white/70 border border-white/20 hover:bg-white/20'
                  )}
                >
                  {tech.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Save Button - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-tg-bg/95 backdrop-blur-xl border-t border-white/10 safe-area-bottom">
        <Button
          onClick={handleSave}
          disabled={!selectedSpecializationId || saving}
          loading={saving}
          className="w-full h-14 bg-white text-black rounded-2xl font-bold hover:bg-neutral-100 transition-all active:scale-95"
        >
          {saving ? 'Сохранение...' : (isFirstTime ? 'Продолжить к тесту' : 'Сохранить и пройти тест')}
        </Button>
      </div>
    </div>
  );
};

export default UpdateSpecializationScreen;

